/**
 * Hook for magic link authentication with Clerk.
 *
 * Handles both login (existing user) and signup verification via email magic links.
 * Listens for session/verification completion via Clerk events.
 *
 * Aliases: magic link hook, email link authentication hook
 */

import { useClerk } from "@clerk/tanstack-react-start";
import { useCallback, useEffect, useRef, useState } from "react";

import { getClerkErrorMessage } from "~/lib/clerk-errors";

export type MagicLinkMode = "login" | "signup";

export type MagicLinkCompletionMethod =
  | "magic_link"
  | "signup"
  | "already_signed_in";

/**
 * Outcome of a `sendMagicLink` call, so callers can decide the next UI state:
 * - `completed`: a session is already active (`onComplete` has fired). Do NOT
 *   show a "check your email" screen.
 * - `awaiting_verification`: an email was sent; show the verification screen and
 *   wait for the link to be clicked.
 */
export type MagicLinkOutcome = "completed" | "awaiting_verification";

export interface MagicLinkSignupData {
  email: string;
  firstName: string;
  lastName: string;
  password?: string;
}

export interface UseMagicLinkAuthResult {
  readonly sendMagicLink: (
    mode: MagicLinkMode,
    emailOrData: string | MagicLinkSignupData,
  ) => Promise<MagicLinkOutcome>;
  readonly isWaiting: boolean;
  readonly isLoading: boolean;
  readonly error: string;
  readonly clearError: () => void;
}

const normalizeEmail = (value: string): string => value.trim().toLowerCase();

export const useMagicLinkAuth = (
  onComplete?: (method: MagicLinkCompletionMethod) => void,
  redirectUrl?: string,
): UseMagicLinkAuthResult => {
  const clerk = useClerk();
  const [isWaiting, setIsWaiting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  // Ensures completion fires at most once per mount. The session poll below and
  // the signup email-link `.then` handler can both observe the same activated
  // session, and firing onComplete twice would double-navigate / double-save.
  const completedRef = useRef(false);

  const clearError = useCallback(() => {
    setError("");
  }, []);

  // Listen for session changes indicating magic link was clicked
  useEffect(() => {
    if (!isWaiting || !clerk) return;

    const checkSession = () => {
      if (clerk.session && !completedRef.current) {
        completedRef.current = true;
        setIsWaiting(false);
        onComplete?.("magic_link");
      }
    };

    // Check immediately
    checkSession();

    // Poll every 2 seconds
    const interval = setInterval(checkSession, 2000);

    return () => {
      clearInterval(interval);
    };
  }, [clerk, isWaiting, onComplete]);

  const sendMagicLink = useCallback(
    async (
      mode: MagicLinkMode,
      emailOrData: string | MagicLinkSignupData,
    ): Promise<MagicLinkOutcome> => {
      // If already signed in, complete immediately
      if (clerk?.session) {
        setIsWaiting(false);
        onComplete?.("already_signed_in");
        return "completed";
      }

      setIsLoading(true);
      setError("");

      try {
        if (!clerk?.client) {
          throw new Error("Clerk client not available");
        }

        if (mode === "login") {
          const email = normalizeEmail(emailOrData as string);

          // Create sign-in attempt
          const signIn = await clerk.client.signIn.create({
            identifier: email,
          });

          // Find email link factor
          const emailLinkFactor = signIn.supportedFirstFactors?.find(
            (factor) => factor.strategy === "email_link",
          );

          if (!emailLinkFactor || !("emailAddressId" in emailLinkFactor)) {
            throw new Error("Magic link authentication is not available");
          }

          // Create and start email link flow
          const emailLinkFlow = signIn.createEmailLinkFlow();
          const flowResult = await emailLinkFlow.startEmailLinkFlow({
            redirectUrl: redirectUrl ?? window.location.href,
            emailAddressId: emailLinkFactor.emailAddressId,
          });

          // Check if sign-in completed immediately
          if (flowResult.status === "complete") {
            const sessionId = flowResult.createdSessionId;
            if (sessionId) {
              await clerk.setActive({ session: sessionId });
              setIsWaiting(false);
              onComplete?.("magic_link");
              return "completed";
            }
          }

          setIsWaiting(true);
          return "awaiting_verification";
        }

        // Signup mode
        const data = emailOrData as MagicLinkSignupData;
        const normalizedEmail = normalizeEmail(data.email);

        // Create signup
        const signUp = await clerk.client.signUp.create({
          emailAddress: normalizedEmail,
          ...(data.password === undefined ? {} : { password: data.password }),
          firstName: data.firstName,
          lastName: data.lastName,
        });

        // A complete signup only satisfies the instance's configured requirements.
        // Passwordless signup needs verified email ownership before activation.
        if (signUp.status === "complete") {
          if (
            data.password === undefined &&
            signUp.verifications.emailAddress.status !== "verified"
          ) {
            throw new Error(
              "Email-link signup is not available yet. Please contact support; your saved result is still available in this browser.",
            );
          }
          if (signUp.createdSessionId) {
            await clerk.setActive({ session: signUp.createdSessionId });
          }
          setIsWaiting(false);
          onComplete?.("signup");
          return "completed";
        }

        // The only requirement this flow can satisfy is verifying the email via
        // magic link. Anything else (extra missing fields) is a configuration
        // mismatch we surface rather than silently claim we emailed the user.
        if (signUp.missingFields.length > 0) {
          throw new Error(
            signUp.missingFields.includes("password") &&
              data.password === undefined
              ? "Passwordless signup is not available yet. Please contact support; your saved result is still available in this browser."
              : "We couldn't finish creating your account. Please contact support.",
          );
        }

        const needsEmailVerification =
          signUp.status === "missing_requirements" &&
          signUp.unverifiedFields.includes("email_address");

        if (!needsEmailVerification) {
          throw new Error(
            "We couldn't finish creating your account. Please try again.",
          );
        }

        // Start the email link flow WITHOUT awaiting it: it sends the email and
        // then polls until the link is clicked. Awaiting here would block us
        // from returning `awaiting_verification` — and the caller from showing
        // the "check your email" screen — until after the link is clicked.
        const { startEmailLinkFlow } = signUp.createEmailLinkFlow();
        void startEmailLinkFlow({
          redirectUrl: redirectUrl ?? window.location.href,
        })
          .then(async (verifiedSignUp) => {
            if (
              verifiedSignUp.status === "complete" &&
              verifiedSignUp.verifications.emailAddress.status === "verified" &&
              verifiedSignUp.createdSessionId &&
              !completedRef.current
            ) {
              // Claim completion BEFORE awaiting setActive: while that promise
              // is in flight the session poll can observe the new session and
              // fire onComplete a second time.
              completedRef.current = true;
              try {
                await clerk.setActive({
                  session: verifiedSignUp.createdSessionId,
                });
              } catch (error: unknown) {
                completedRef.current = false;
                throw error;
              }
              setIsWaiting(false);
              onComplete?.("magic_link");
            } else if (!completedRef.current) {
              throw new Error(
                "We couldn't finish creating your account. Please try again.",
              );
            }
          })
          .catch((err: unknown) => {
            setError(
              getClerkErrorMessage(err, "Failed to verify your email link"),
            );
            setIsWaiting(false);
          });

        setIsWaiting(true);
        return "awaiting_verification";
      } catch (err: unknown) {
        const message = getClerkErrorMessage(
          err,
          mode === "login"
            ? "Failed to send sign-in link"
            : "Failed to send verification link",
        );
        setError(message);
        setIsWaiting(false);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [clerk, onComplete, redirectUrl],
  );

  return {
    sendMagicLink,
    isWaiting,
    isLoading,
    error,
    clearError,
  };
};
